// @flow
import { object, array, number } from 'yup';
import { getIsEstimateRenewal } from '@src/custom-hooks/useIsEstimateRenewal';
import { getPreviousARRFromEstimate } from '@src/custom-hooks/usePreviousContractDetails';
import { getAvgSpendAfterDiscount } from '@src/custom-hooks/useEstimateACVAfterDiscount';
import { getStarterCommitLimits } from '@src/custom-hooks/useStarterCommitLimits';

import {
  CREDITS_AND_BURSTS_BURSTS_AMOUNT_BACKEND_NAME,
  CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME,
  CREDITS_AND_BURSTS_CREDITS_AMOUNT_BACKEND_NAME,
  CREDITS_AND_BURSTS_JSON_BACKEND_NAME,
  CREDITS_AND_BURSTS_YEAR_NUMBER_BACKEND_NAME,
  CREDITS_AND_BURSTS_MAX_CONFIGURABLE_LIMIT,
  IS_SGCNBC,
  IS_ROW_OPENED_FOR_EDITING,
  IS_RENEWAL_AUTOMATION_LIVE,
} from '../constants';
import {
  getIfElemRepresentsASGCNBCRow,
  getIsNetNewCustomer,
} from '../components/credits-and-bursts/utils';
import { getTotalCommitAfterDiscount } from '../triggers/TCVApprovalRequiredContainer';
import { formatCurrency0DP, formatFloat0DP } from '../components/views/grid-utils/renderers';
import {
  getNetNewCommit,
  convertConfigArrayToFrontEndFormat,
  getDealDurationInYears,
  getTotalCommitmentDollarsDiscountedForTheGivenYear,
} from '../common-utils/utils';
import { SELECT_INPUT_TYPE, TEXT_NUMBER_INPUT_TYPE } from '../formik-utils/consts';

import { convertConfigArrayToMap } from './utils';

export const getMaxAllowedCredits = (estimate) => {
  const isRenewal = getIsEstimateRenewal(estimate);

  if (isRenewal) {
    // For Renewals: 15% of Net New commit for each year
    // For down-sells (negative Net New), limit is set to 0
    const totalYears = getDealDurationInYears(estimate);
    const previousARR = getPreviousARRFromEstimate(estimate);
    const avgSpendAfterDiscount = getAvgSpendAfterDiscount(estimate);
    const netNewCommit = getNetNewCommit(avgSpendAfterDiscount, previousARR);
    return Math.max(0, netNewCommit * 0.15 * totalYears);
  }
  // For New Customers: 15% of total commit after discount
  const totalCommitAfterDiscount = getTotalCommitAfterDiscount(estimate);
  return totalCommitAfterDiscount * 0.15;
};

// Treats a credits/bursts amount that is null, undefined, '' (input cleared while editing) or NaN as 0,
// so a half-filled row doesn't break the total. That handling is specific to the credits and bursts
// rows, hence this stays here instead of moving to common utils.
const getSumOfCreditsOrBurstsAcrossAllYears = (allRows, amountFieldName) =>
  allRows.reduce((sum, config) => sum + (config[amountFieldName] || 0), 0);

export const cnbcValidationSchemaFunc = (estimate) => {
  const totalYears = Number(estimate?.deal_duration?.deal_duration_years ?? 0);
  const isUserAdmin = estimate?.isUserAdmin;
  const isNetNewCustomer = getIsNetNewCustomer(estimate);
  const isRenewal = getIsEstimateRenewal(estimate);

  return object({
    [CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME]: array(
      object({
        [CREDITS_AND_BURSTS_YEAR_NUMBER_BACKEND_NAME]: number()
          .label('Year Number')
          .typeError('Year Number is required!')
          .min(1, 'Value should be greater than or equal to 1')
          .max(
            totalYears,
            `This year is outside of the range of the estimate (  >=1 and <= ${totalYears} ). Please delete this one or choose a year within the range! `
          )
          .required(),

        [CREDITS_AND_BURSTS_CREDITS_AMOUNT_BACKEND_NAME]: number()
          .integer()
          .label('Credits Amount')
          .typeError('Credits Amount is required!')
          .min(0)
          .max(
            CREDITS_AND_BURSTS_MAX_CONFIGURABLE_LIMIT,
            'Cannot add more than 1 billion of credits!'
          )
          .required()
          .test(
            'testCreditsLessThanDiscountedCommitAmountForTheYear',
            'The credit amount cannot be greater than the discounted commit amount for this year!',
            function (value) {
              const yearNumber = this.parent[CREDITS_AND_BURSTS_YEAR_NUMBER_BACKEND_NAME];

              const commitmentDollarsDiscountedForTheGivenYear =
                getTotalCommitmentDollarsDiscountedForTheGivenYear(estimate, yearNumber);

              return value <= commitmentDollarsDiscountedForTheGivenYear;
            }
          )
          .test(
            'testSumOfCreditsLimit',
            'The sum of credits cannot exceed the allowed limit!',
            (value, { from, createError }) => {
              // Skip validation if User is not a Net New Customer
              if (!isNetNewCustomer) {
                return true;
              }

              // Skip validation if User is Admin
              if (isUserAdmin) {
                return true;
              }

              // Get current row being validated
              const currRow = from[0].value;
              // Only validate the row which is in progress (not frozen)
              if (currRow?.[IS_ROW_OPENED_FOR_EDITING] === false) {
                return true;
              }

              const parentFormValues = from[1].value;
              const allRows = parentFormValues[CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME] || [];

              // Skip validation if no rows are configured
              if (allRows.length === 0) {
                return true;
              }

              // Calculate sum of credits across all years
              const sumOfCredits = allRows.reduce(
                (sum, config) =>
                  sum + (config[CREDITS_AND_BURSTS_CREDITS_AMOUNT_BACKEND_NAME] || 0),
                0
              );

              const totalCommitAfterDiscount = getTotalCommitAfterDiscount(estimate);
              const maxAllowedCredits = IS_RENEWAL_AUTOMATION_LIVE
                ? getMaxAllowedCredits(estimate)
                : totalCommitAfterDiscount * 0.15;

              const roundedMaxAllowedCredits = formatFloat0DP(maxAllowedCredits);

              /*
               * We are using the rounded max allowed credits because, we are only allowing the user to input integers for credits and bursts.
               * Even in the validation message, we are using the rounded max allowed credits. Hence, to keep it consistent, we are using the rounded max allowed credits.
               */
              if (sumOfCredits > roundedMaxAllowedCredits) {
                const formattedSumOfCredits = formatCurrency0DP.format(sumOfCredits);
                const formattedMaxAllowedCredits = formatCurrency0DP.format(maxAllowedCredits);

                if (IS_RENEWAL_AUTOMATION_LIVE && isRenewal) {
                  return createError({
                    message: `The sum of credits across all years (${formattedSumOfCredits}) cannot exceed 15% of Net New commit for ${totalYears} years (${formattedMaxAllowedCredits})!`,
                  });
                } else {
                  return createError({
                    message: `The sum of credits across all years (${formattedSumOfCredits}) cannot exceed 15% of the total commit value after discount (${formattedMaxAllowedCredits})!`,
                  });
                }
              }

              return true;
            }
          )
          .test(
            'testSumOfCreditsWithinStarterCommitLimit',
            'The sum of credits cannot exceed the CC Starter Commit limit!',
            (value, { from, createError }) => {
              const starterCommitLimits = getStarterCommitLimits(estimate);
              if (!starterCommitLimits) {
                return true;
              }

              // Only validate the row which is in progress (not frozen)
              const currRow = from[0].value;
              if (currRow?.[IS_ROW_OPENED_FOR_EDITING] === false) {
                return true;
              }

              // No admin bypass: credits that take the invoiced amount below the package minimum
              // undercut what the customer is guaranteed to pay for the package.
              const allRows = from[1].value[CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME] || [];
              const sumOfCredits = getSumOfCreditsOrBurstsAcrossAllYears(
                allRows,
                CREDITS_AND_BURSTS_CREDITS_AMOUNT_BACKEND_NAME
              );
              const maxTotalCredits = starterCommitLimits.max_total_credits;

              if (sumOfCredits <= maxTotalCredits) {
                return true;
              }

              const formattedSumOfCredits = formatCurrency0DP.format(sumOfCredits);
              const formattedMinimumCommit = formatCurrency0DP.format(
                starterCommitLimits.minimum_commit
              );
              const formattedMaxTotalCredits = formatCurrency0DP.format(maxTotalCredits);

              return createError({
                message: `The sum of credits across all years (${formattedSumOfCredits}) would bring the amount invoiced to the customer below the package minimum of ${formattedMinimumCommit}. Maximum credits allowed: ${formattedMaxTotalCredits}!`,
              });
            }
          ),

        [CREDITS_AND_BURSTS_BURSTS_AMOUNT_BACKEND_NAME]: number()
          .integer()
          .label('Bursts Amount')
          .typeError('Bursts Amount is required!')
          .min(0)
          .max(
            CREDITS_AND_BURSTS_MAX_CONFIGURABLE_LIMIT,
            'Cannot add more than 1 billion of bursts!'
          )
          .required()
          .test(
            'testBurstsOnlyOnceStarterCommitMinimumIsSized',
            'Bursts can be added only once the sizing reaches the CC Starter Commit package minimum!',
            (value, { from, createError }) => {
              const starterCommitLimits = getStarterCommitLimits(estimate);
              if (!starterCommitLimits) {
                return true;
              }

              // Admins may still offer bursts on an under-sized starter package
              if (isUserAdmin) {
                return true;
              }

              // Only validate the row which is in progress (not frozen)
              const currRow = from[0].value;
              if (currRow?.[IS_ROW_OPENED_FOR_EDITING] === false) {
                return true;
              }

              // Non-admins can add bursts only once the Unused CC Starter Commit Spend is $0.
              // This check is the same thing: total_commit_after_discount includes the Streamhouse
              // Accelerator pack but not the unused top-up, and minimum_commit is the package minimum
              // plus that same pack. With the pack on both sides, it reduces to "spend outside the
              // pack >= package minimum", which is exactly when nothing is left unused.
              // After that, the package maximum check below decides how much is allowed.
              const isPackageMinimumSized =
                starterCommitLimits.total_commit_after_discount >=
                starterCommitLimits.minimum_commit;
              if (isPackageMinimumSized) {
                return true;
              }

              const allRows = from[1].value[CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME] || [];
              const sumOfBursts = getSumOfCreditsOrBurstsAcrossAllYears(
                allRows,
                CREDITS_AND_BURSTS_BURSTS_AMOUNT_BACKEND_NAME
              );
              if (sumOfBursts === 0) {
                return true;
              }

              const formattedMinimumCommit = formatCurrency0DP.format(
                starterCommitLimits.minimum_commit
              );

              return createError({
                message: `Bursts can be added only once the sizing reaches the package minimum of ${formattedMinimumCommit}!`,
              });
            }
          )
          .test(
            'testSumOfBurstsWithinStarterCommitLimit',
            'The sum of bursts cannot exceed the CC Starter Commit limit!',
            (value, { from, createError }) => {
              const starterCommitLimits = getStarterCommitLimits(estimate);
              if (!starterCommitLimits) {
                return true;
              }

              // Only validate the row which is in progress (not frozen)
              const currRow = from[0].value;
              if (currRow?.[IS_ROW_OPENED_FOR_EDITING] === false) {
                return true;
              }

              // No admin bypass: bursts that take the total commit above the package maximum mean
              // the deal has outgrown the starter package.
              const allRows = from[1].value[CREDITS_AND_BURSTS_CONFIG_ARRAY_BACKEND_NAME] || [];
              const sumOfBursts = getSumOfCreditsOrBurstsAcrossAllYears(
                allRows,
                CREDITS_AND_BURSTS_BURSTS_AMOUNT_BACKEND_NAME
              );
              const maxTotalBursts = starterCommitLimits.max_total_bursts;

              if (sumOfBursts <= maxTotalBursts) {
                return true;
              }

              const formattedSumOfBursts = formatCurrency0DP.format(sumOfBursts);
              const formattedMaximumCommit = formatCurrency0DP.format(
                starterCommitLimits.maximum_commit
              );
              const formattedMaxTotalBursts = formatCurrency0DP.format(maxTotalBursts);

              return createError({
                message: `The sum of bursts across all years (${formattedSumOfBursts}) would take the total commit above the package maximum of ${formattedMaximumCommit}. Maximum bursts allowed: ${formattedMaxTotalBursts}!`,
              });
            }
          ),
      })
    )
      .notRequired()
      .default([]),
  });
};

export const CREDITS_AND_BURSTS_JSON_CONFIG = {
  backendFieldName: CREDITS_AND_BURSTS_JSON_BACKEND_NAME,
  inputTransformationFunc: (x) => {
    // todo:: Get these as numbers from the backend
    let newRows = x.cnbc_configs.map((elem) => ({
      ...elem,
      credit_amount: Number(elem.credit_amount),
      burst_amount: Number(elem.burst_amount),
      // Added IS_SGCNBC similar to how it's done in custom-discounts for IS_VBD
      // This makes easier to identify if the row is a SGCNBC row.
      [IS_SGCNBC]: getIfElemRepresentsASGCNBCRow(elem),
    }));
    newRows = convertConfigArrayToFrontEndFormat(newRows);
    return { cnbc_configs: newRows };
  },
  validation: cnbcValidationSchemaFunc,
};

export const CREDITS_AND_BURSTS_CONFIG_ARRAY_CONFIG = [
  {
    backendFieldName: CREDITS_AND_BURSTS_YEAR_NUMBER_BACKEND_NAME,
    displayName: 'Year',
    hidden: false,
    columnWidth: 3,
    colDefaultValue: null,
    type: SELECT_INPUT_TYPE,
  },
  {
    backendFieldName: CREDITS_AND_BURSTS_CREDITS_AMOUNT_BACKEND_NAME,
    hidden: false,
    displayName: 'Credits',
    columnWidth: 2,
    colDefaultValue: 0,
    icon: 'dollar',
    type: TEXT_NUMBER_INPUT_TYPE,
  },
  {
    backendFieldName: CREDITS_AND_BURSTS_BURSTS_AMOUNT_BACKEND_NAME,
    hidden: false,
    displayName: 'Burst Capacity',
    columnWidth: 2,
    colDefaultValue: 0,
    icon: 'dollar',
    type: TEXT_NUMBER_INPUT_TYPE,
  },
];

export const CREDITS_AND_BURSTS_CONFIG_ARRAY_CONFIG_MAP = convertConfigArrayToMap(
  CREDITS_AND_BURSTS_CONFIG_ARRAY_CONFIG
);
